import React, { useState } from "react";
import { IconButton, Popover, Stack } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";

/**
 * Kebab (three vertical dots) trigger for table row actions.
 *
 * Replaces inline rows of icon buttons so the Actions column stays narrow.
 * Clicking the dots pops the same icons out in a single horizontal row.
 *
 * @param {Array} actions - one entry per action:
 *   { label, icon, color, onClick, disabled, hidden }
 *   - label: accessible name for the icon, also the React key so keep it
 *     unique within a row
 *   - icon: element, e.g. <EditIcon />
 *   - color: applied to the icon so the existing color coding is kept
 *   - hidden: true drops the entry (use for permission checks)
 * @param {string} ariaLabel - label for the trigger button
 * @param {boolean} disabled - disables the trigger button
 */
const RowActionsMenu = ({
  actions = [],
  ariaLabel = "row actions",
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const visibleActions = actions.filter(
    (action) => action && action.hidden !== true
  );

  // stopPropagation keeps row-level handlers (selection, sorting, row click)
  // from firing when the user only meant to open the actions
  const handleOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    if (event) event.stopPropagation();
    setAnchorEl(null);
  };

  const handleActionClick = (event, action) => {
    event.stopPropagation();
    setAnchorEl(null);
    if (action.onClick) action.onClick(event);
  };

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <>
      <IconButton
        aria-label={ariaLabel}
        size="small"
        disabled={disabled}
        onClick={handleOpen}
      >
        <MoreVertIcon />
      </IconButton>
      {/* Flies out to the left of the dots - Actions is the last column, so a
          horizontal row opening rightwards would run off the table */}
      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "center", horizontal: "left" }}
        transformOrigin={{ vertical: "center", horizontal: "right" }}
      >
        <Stack direction="row" spacing={0.5} sx={{ px: 1, py: 0.5 }}>
          {visibleActions.map((action) => (
            <IconButton
              key={action.label}
              aria-label={action.label}
              disabled={action.disabled}
              size="small"
              sx={{ color: action.color }}
              onClick={(e) => handleActionClick(e, action)}
            >
              {action.icon}
            </IconButton>
          ))}
        </Stack>
      </Popover>
    </>
  );
};

export default RowActionsMenu;
